import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { SKIP_AUTH_ERROR_LOGOUT } from '../interceptors/auth-error.interceptor';

@Injectable({ providedIn: 'root' })
export class TimesheetService {
	private baseUrl = `${environment.urls.otherApiUrl_Obj}timesheet/`;

	constructor(private _http: HttpClient) { }

	// Appends a cache-busting param so every GET is a distinct URL. UAT sits
	// behind caching (CDN/Front Door and/or the browser) that was serving
	// stale responses even across a full page reload - this forces a fresh
	// fetch from origin every time regardless of what's in front of it.
	private noCache(url: string, hasQuery: boolean): string {
		return `${url}${hasQuery ? '&' : '?'}_ts=${Date.now()}`;
	}

	// Talent-side reads: assignments are managed from the Hire app; Talent
	// only looks up its own assignment(s) to check entitlement/status.
	// skipAuthErrorLogout must be opt-in per call site, NOT baked in here —
	// the header's best-effort entitlement check wants a real 401 to fail
	// quietly, but the Timesheet page's own primary data load must still
	// log the user out on a genuine expired session, not swallow it.
	getMyAssignments(talentUserId: number, skipAuthErrorLogout: boolean = false) {
		const options = skipAuthErrorLogout
			? { context: new HttpContext().set(SKIP_AUTH_ERROR_LOGOUT, true) }
			: {};
		return this._http.get(this.noCache(`${this.baseUrl}assignments?talentUserId=${talentUserId}`, true), options);
	}

	// ── Timesheet entry & submission ───────────────────────────────────────────

	getCurrentTimesheet(assignmentId: number, year?: number, month?: number) {
		let url = `${this.baseUrl}timesheets/current?assignmentId=${assignmentId}`;
		if (year) url += `&year=${year}`;
		if (month) url += `&month=${month}`;
		return this._http.get(this.noCache(url, true));
	}

	// Every period across every assignment this Talent holds, newest first, in
	// the same row shape as the per-assignment list. Retires the forkJoin that
	// fanned out one request per assignment.
	getMyTimesheets() {
		return this._http.get(this.noCache(`${this.baseUrl}timesheets/mine`, false));
	}

	getTimesheetHistory(assignmentId: number) {
		return this._http.get(this.noCache(`${this.baseUrl}assignments/${assignmentId}/timesheets`, false));
	}

	upsertEntries(timesheetId: number, entries: { workDate: string; hoursWorked: number; notes?: string }[]) {
		return this._http.put(`${this.baseUrl}timesheets/${timesheetId}/entries`, { Entries: entries });
	}

	submitTimesheet(timesheetId: number, rowVersion: string, signerName: string, signatureImageBase64: string) {
		return this._http.post(`${this.baseUrl}timesheets/${timesheetId}/submit`, { RowVersion: rowVersion, SignerName: signerName, SignatureImageBase64: signatureImageBase64 });
	}

	// Self-certified assignments only (Authorized Person Approval off):
	// pulls an already-submitted timesheet back to Draft so the talent can
	// fix their own hours. Allowed only until a signed copy is uploaded -
	// after that it is locked and only a Hire user can release it.
	reviseTimesheet(timesheetId: number, rowVersion: string) {
		return this._http.post(`${this.baseUrl}timesheets/${timesheetId}/revise`, { RowVersion: rowVersion });
	}

	// Throws an approved period away and starts it over: returns a NEW Draft
	// for the same month with the hours copied across and no signature, PDF or
	// uploaded copy. The response carries a DIFFERENT timesheetId - the old one
	// is dead the moment this returns, so callers must switch to the new one.
	restartTimesheet(timesheetId: number, rowVersion: string) {
		return this._http.post(`${this.baseUrl}timesheets/${timesheetId}/restart`, { RowVersion: rowVersion });
	}

	// A photo/scan of the physically-signed printout. Two setups use it:
	// the Print approval method (an Authorized Person signs on paper) and a
	// self-certified assignment (no Authorized Person at all - the talent
	// gets the Project Manager's signature offline). Either way this upload
	// is what completes and locks the timesheet.
	uploadSignedDocument(timesheetId: number, file: File, rowVersion: string) {
		const fd = new FormData();
		fd.append('file', file);
		// Appending an undefined rowVersion would post the literal text
		// "undefined", so normalise first - the server reports a null rowVersion
		// as "Value cannot be null. (Parameter 's')" behind a 404, and a bogus
		// token would be just as unhelpful.
		const token = (rowVersion ?? '').toString();
		// Sent under both spellings: IFormCollection matches case-insensitively,
		// but a handler reading the raw key with an ordinal comparer does not,
		// and the API documents this field as PascalCase.
		fd.append('rowVersion', token);
		fd.append('RowVersion', token);
		return this._http.post(`${this.baseUrl}timesheets/${timesheetId}/upload-signed-document`, fd);
	}

	// Removes the talent's uploaded signed copy so they can replace it with
	// a corrected one. Requires a Talent-accessible backend route.
	deleteSignedDocument(timesheetId: number, rowVersion: string) {
		return this._http.post(`${this.baseUrl}timesheets/${timesheetId}/delete-signed-document`, { RowVersion: rowVersion });
	}

	// Informal "here's my hours for this week" FYI to the recruiter -
	// separate from Submit/Approve. Locks the whole date range once sent.
	sendWeekReport(timesheetId: number, weekStartDate: string, weekEndDate: string) {
		return this._http.post(`${this.baseUrl}timesheets/${timesheetId}/send-week-report`, { WeekStartDate: weekStartDate, WeekEndDate: weekEndDate });
	}

	getHolidays(region?: string, year?: number) {
		const params: string[] = [];
		if (region) params.push(`region=${encodeURIComponent(region)}`);
		if (year) params.push(`year=${year}`);
		let url = `${this.baseUrl}holidays`;
		if (params.length) url += `?${params.join('&')}`;
		return this._http.get(this.noCache(url, params.length > 0));
	}

	// ── Header badge: new Timesheet access ──────────────────────────────────────

	// Same skip-logout-on-401 treatment as getMyAssignments() - this is a
	// best-effort header badge fetch, not the primary data load.
	getUnseenActivationCount(skipAuthErrorLogout: boolean = false) {
		const options = skipAuthErrorLogout
			? { context: new HttpContext().set(SKIP_AUTH_ERROR_LOGOUT, true) }
			: {};
		return this._http.get(this.noCache(`${this.baseUrl}assignments/unseen-activation-count`, false), options);
	}

	markActivationsSeen() {
		return this._http.post(`${this.baseUrl}assignments/mark-activations-seen`, {});
	}

	resendApprovalEmail(timesheetId: number) {
		return this._http.post(`${this.baseUrl}timesheets/${timesheetId}/resend-approval-email`, {});
	}

}
